import React from "react";
import NoImage from "../../assets/images/No_Image_Available.jpg";
import TanzaniaImage from "../../assets/images/tanzania.jpg";
import KenyaImage from "../../assets/images/kenya.jpg";
import RwandaImage from "../../assets/images/rwanda.jpg";
import UgandaImage from "../../assets/images/uganda.jpg";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Autoplay, Navigation, EffectCoverflow } from "swiper/modules";
import { Link, useNavigate } from "react-router-dom";
import LazyImage from "../image/LazyLoadImage";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Mountain } from "lucide-react";

const HeroComponentRedesign = ({ destinations }) => {
  const navigate = useNavigate();

  const handleCountryClick = (countryCode) => {
    navigate(`/country/destinations/${countryCode}`);
  };

  // Hero slider content with different Unsplash backgrounds
  const heroSlides = [
    {
      id: 1,
      badge: "EAST AFRICA'S PREMIER HIKING COMMUNITY",
      icon: Mountain,
      title: "Unleash the",
      titleHighlight: "Hiker",
      titleEnd: "Within",
      description: "Every individual, regardless of experience, possesses an innate ability to conquer the most challenging peaks. Discover your potential with us.",
      background: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80", // Mountain landscape
      primaryCTA: "Hike With Us",
      primaryLink: "/events",
      secondaryCTA: "Shop With Us",
      secondaryLink: "https://www.instagram.com/mambahikers_gear_shop?igsh=MThtc28xdGNuaWNqcw==",
      isExternal: true,
    },
    {
      id: 2,
      badge: "ADVENTURE AWAITS",
      icon: Mountain,
      title: "Conquer the",
      titleHighlight: "Summit",
      titleEnd: "Together",
      description: "Join Africa’s most vibrant hiking community. Thousands of adventurers have already found their tribe. Will you be next?",
      background: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1920&q=80", // Mountain peak
      primaryCTA: "Join Community",
      primaryLink: "/events",
      secondaryCTA: "View Programs",
      secondaryLink: "/programmes",
      isExternal: false,
    },
    {
      id: 3,
      badge: "GUIDED EXPEDITIONS",
      icon: Mountain,
      title: "Experience",
      titleHighlight: "Epic",
      titleEnd: "Adventures",
      description: "Expert guides, medical support, and perfectly planned routes across Tanzania, Kenya, Rwanda, and Uganda. Your safety is our priority.",
      background: "https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=1920&q=80", // Hiking trail
      primaryCTA: "View Destinations",
      primaryLink: "/destinations",
      secondaryCTA: "Meet Our Guides",
      secondaryLink: "/about",
      isExternal: false,
    },
    {
      id: 4,
      badge: "EXPLORE EAST AFRICA",
      icon: Mountain,
      title: "Discover",
      titleHighlight: "Hidden",
      titleEnd: "Trails",
      description: "From Rwanda's volcanic peaks to Tanzania's legendary Kilimanjaro. Explore breathtaking landscapes with experienced guides who know every path.",
      background: "https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?w=1920&q=80", // Mountain vista
      primaryCTA: "Explore Routes",
      primaryLink: "/destinations",
      secondaryCTA: "Book Now",
      secondaryLink: "/events",
      isExternal: false,
    },
  ];

  // Slider state
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [slideProgress, setSlideProgress] = React.useState(0);
  const slideDuration = 7000; // 7 seconds per slide

  // Auto-advance slider
  React.useEffect(() => {
    const progressInterval = setInterval(() => {
      setSlideProgress((prev) => {
        if (prev >= 100) {
          setCurrentSlide((current) => (current + 1) % heroSlides.length);
          return 0;
        }
        return prev + (100 / (slideDuration / 50));
      });
    }, 50);
    return () => clearInterval(progressInterval);
  }, [heroSlides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setSlideProgress(0);
  };

  const currentSlideData = heroSlides[currentSlide];
  const SlideIcon = currentSlideData.icon;

  // Define countries with their specific images and correct country codes
  const countries = [
    {
      id: 1,
      name: 'Tanzania',
      value: 'Tanzania',
      image: TanzaniaImage
    },
    {
      id: 2,
      name: 'Kenya',
      value: 'Kenya',
      image: KenyaImage
    },
    {
      id: 3,
      name: 'Rwanda',
      value: 'Rwanda',
      image: RwandaImage
    },
    {
      id: 4,
      name: 'Uganda',
      value: 'Uganda',
      image: UgandaImage
    }
  ];

  return (
    <div className="min-h-[550px] sm:min-h-[600px] md:min-h-[650px] lg:min-h-[650px] rounded-lg relative w-full overflow-hidden">
      {/* Dynamic Background with Crossfade */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${currentSlideData.background})`,
          }}
        />
      </AnimatePresence>

      {/* Enhanced gradient overlays - Mamba Theme */}
      <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/85 via-slate-900/75 to-mamba-navy/70"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/90 via-transparent to-transparent"></div>

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute -top-40 -right-40 w-96 h-96 bg-mamba-sky-blue/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute top-1/3 -left-40 w-96 h-96 bg-mamba-golden/20 rounded-full blur-3xl"
        />
      </div>

      {/* Headline with enhanced animations and dynamic content */}
      <div className="text-center pt-12 sm:pt-16 md:pt-20 lg:pt-28 xl:pt-36 relative z-10 px-4 pb-[100px] sm:pb-[150px] md:pb-[200px] lg:pb-[300px]">
        {/* Small badge above title */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`badge-${currentSlide}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 mb-3 sm:mb-4 md:mb-6 px-3 sm:px-4 py-1.5 sm:py-2 bg-mamba-golden/20 backdrop-blur-md border border-mamba-golden/40 rounded-full"
          >
            <SlideIcon className="w-3 h-3 sm:w-4 sm:h-4 text-mamba-golden" />
            <span className="text-xs sm:text-sm text-white font-montserrat font-bold tracking-wide">{currentSlideData.badge}</span>
          </motion.div>
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.div
            key={`title-${currentSlide}`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8 }}
            className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-montserrat font-black text-white mb-3 sm:mb-4 md:mb-6 tracking-tight leading-tight"
          >
            {currentSlideData.title}{" "}
            <span className="bg-gradient-to-r from-mamba-golden via-mamba-sky-blue to-mamba-sky-blue bg-clip-text text-transparent">
              {currentSlideData.titleHighlight}
            </span>
            <br />
            {currentSlideData.titleEnd}
          </motion.div>
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.div
            key={`desc-${currentSlide}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="my-4 sm:my-5 md:my-6 lg:my-8 mx-auto max-w-3xl text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-slate-200 leading-relaxed font-inter px-2"
          >
            {currentSlideData.description}
          </motion.div>
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.div
            key={`cta-${currentSlide}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 md:gap-5 lg:gap-6 px-2 mb-4 sm:mb-6 md:mb-8"
          >
            {currentSlideData.isExternal ? (
              <Link to={currentSlideData.primaryLink} className="w-full sm:w-auto max-w-xs">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="group w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-mamba-golden to-mamba-sky-blue text-white text-sm sm:text-base font-montserrat font-bold rounded-2xl shadow-2xl hover:shadow-mamba-golden/50 transition-all duration-300 overflow-hidden relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  <span className="relative flex items-center justify-center gap-2">
                    {currentSlideData.primaryCTA}
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
              </Link>
            ) : (
              <Link to={currentSlideData.primaryLink} className="w-full sm:w-auto max-w-xs">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="group w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-mamba-golden to-mamba-sky-blue text-white text-sm sm:text-base font-montserrat font-bold rounded-2xl shadow-2xl hover:shadow-mamba-golden/50 transition-all duration-300 overflow-hidden relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  <span className="relative flex items-center justify-center gap-2">
                    {currentSlideData.primaryCTA}
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
              </Link>
            )}

            {currentSlideData.secondaryLink.startsWith('http') ? (
              <a
                href={currentSlideData.secondaryLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto max-w-xs"
              >
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="group w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-md border-2 border-mamba-golden/40 text-white text-sm sm:text-base font-montserrat font-bold rounded-2xl hover:bg-mamba-golden/20 hover:border-mamba-golden transition-all duration-300"
                >
                  <span className="flex items-center justify-center gap-2">
                    {currentSlideData.secondaryCTA}
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
              </a>
            ) : (
              <Link to={currentSlideData.secondaryLink} className="w-full sm:w-auto max-w-xs">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="group w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-md border-2 border-mamba-golden/40 text-white text-sm sm:text-base font-montserrat font-bold rounded-2xl hover:bg-mamba-golden/20 hover:border-mamba-golden transition-all duration-300"
                >
                  <span className="flex items-center justify-center gap-2">
                    {currentSlideData.secondaryCTA}
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
              </Link>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Slider Navigation Dots */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="flex justify-center gap-2 sm:gap-3 mt-6 sm:mt-8"
        >
          {heroSlides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => goToSlide(index)}
              className="group relative"
              aria-label={`Go to slide ${index + 1}`}
            >
              <div className={`h-1 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'w-12 bg-mamba-golden' : 'w-8 bg-white/40 group-hover:bg-white/60'
              }`}>
                {index === currentSlide && (
                  <motion.div
                    className="h-full bg-gradient-to-r from-mamba-golden to-mamba-sky-blue rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: `${slideProgress}%` }}
                    transition={{ duration: 0.05 }}
                  />
                )}
              </div>
            </button>
          ))}
        </motion.div>
      </div>

      {/* Destination section with enhanced styling */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.8 }}
        className="absolute bottom-0 py-6 sm:py-8 px-4 sm:px-6 md:px-16 lg:px-32 flex flex-col md:flex-row justify-between items-center w-full z-10 space-y-4 sm:space-y-6 md:space-y-0"
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/80 to-transparent backdrop-blur-sm pointer-events-none"></div>
        
        <div className="md:flex-1 text-center md:text-left z-10 pointer-events-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            {/* Small label - Mamba Theme */}
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 bg-mamba-golden/20 border border-mamba-golden/40 rounded-full">
              <div className="w-2 h-2 bg-mamba-golden rounded-full animate-pulse" />
              <span className="text-xs text-mamba-golden font-montserrat font-bold tracking-wider">FEATURED DESTINATIONS</span>
            </div>
            
            <div className="text-xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-montserrat font-black text-white mb-2 sm:mb-3 tracking-tight">
              Tanzania, Kenya,<br className="hidden sm:block" /> Rwanda & Uganda
            </div>
            <div className="text-slate-200 text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl leading-relaxed font-inter">
              Explore the best hiking destinations across East Africa
            </div>
          </motion.div>
        </div>

        {/* DESTINATIONS with enhanced cards */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="md:flex-1 w-full md:w-auto overflow-hidden z-10 pointer-events-auto"
        >
          <Swiper
            effect={"coverflow"}
            slidesPerView={1}
            breakpoints={{
              300: { slidesPerView: 2 },
              640: { slidesPerView: 2 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            spaceBetween={20}
            coverflowEffect={{
              rotate: 50,
              stretch: 0,
              depth: 100,
              modifier: 1,
              slideShadows: true,
            }}
            loop={true}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            navigation={true}
            modules={[EffectCoverflow, Autoplay, Navigation]}
          >
            {countries?.map((country, index) => (
              <SwiperSlide key={index}>
                <div className="relative group">
                  {/* Image with overlay */}
                  <div 
                    className="relative overflow-hidden rounded-2xl cursor-pointer transition-transform duration-300 hover:-translate-y-2"
                    onClick={() => handleCountryClick(country.value)}
                  >
                    <LazyImage
                      src={country.image || NoImage}
                      className="h-48 sm:h-64 md:h-80 w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent opacity-80 group-hover:opacity-60 transition-opacity duration-300" />
                    
                    {/* Shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  </div>

                  {/* Country name with enhanced styling */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-montserrat font-black text-lg sm:text-xl md:text-2xl mb-1 tracking-tight">
                          {country.name}
                        </div>
                        <div className="text-mamba-golden text-xs sm:text-sm font-semibold flex items-center gap-1">
                          Explore Now
                          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center group-hover:bg-mamba-golden group-hover:border-mamba-golden transition-all duration-300">
                        <ArrowRight className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default HeroComponentRedesign;
